import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Siren, Phone, MapPin, Truck } from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { trpc } from '@/trpc/client';
import { useGeolocation } from '@/hooks/useGeolocation';
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface RecoveryCompany {
  id: string;
  name: string;
  phone: string;
  phoneTwo?: string | null;
  email?: string | null;
  address?: string | null;
  district: string;
  latitude?: number | null;
  longitude?: number | null;
  isActive: boolean;
  services: string[];
  description?: string | null;
  distanceKm: number;
}

interface SOSButtonProps {
    jobId: string;
    onSuccess?: () => void;
}

const SOSButton = ({ jobId, onSuccess }: SOSButtonProps) => {
    const [open, setOpen] = useState(false);
    const [showRecoveryCompanies, setShowRecoveryCompanies] = useState(false);
    const [nearbyCompanies, setNearbyCompanies] = useState<RecoveryCompany[]>([]);
    const { location } = useGeolocation();

    const triggerSOS = trpc.sos.trigger.useMutation({
        onSuccess: (data) => {
            setOpen(false);
            if (data.nearbyRecoveryCompanies && data.nearbyRecoveryCompanies.length > 0) {
                setNearbyCompanies(data.nearbyRecoveryCompanies);
                setShowRecoveryCompanies(true);
            } else {
                toast.error("SOS Signal Broadcasted! No recovery companies found nearby.");
            }
            onSuccess?.();
        },
        onError: (error) => {
            toast.error("Failed to trigger SOS: " + error.message);
        }
    });

    const handleConfirm = () => {
        if (!location) {
            // Even if location missing, we try to send (backend might handle or fail, but emergency is emergency)
            triggerSOS.mutate({ jobId, lat: 0, lng: 0, reason: "Emergency Button Triggered (No GPS)" });
            return;
        }
        triggerSOS.mutate({
            jobId,
            lat: location.latitude,
            lng: location.longitude,
            reason: "Driver Emergency Button Triggered"
        });
    };

    const handleCall = (phoneNumber: string) => {
        window.location.href = `tel:${phoneNumber}`;
    };

    const availableServices = [
        { code: 'TOWING', label: 'Towing', color: 'bg-blue-100 text-blue-800' },
        { code: 'BATTERY_JUMP', label: 'Battery Jump', color: 'bg-yellow-100 text-yellow-800' },
        { code: 'TIRE_CHANGE', label: 'Tire Change', color: 'bg-green-100 text-green-800' },
        { code: 'FUEL_DELIVERY', label: 'Fuel Delivery', color: 'bg-orange-100 text-orange-800' },
        { code: 'ACCIDENT_RECOVERY', label: 'Accident Recovery', color: 'bg-red-100 text-red-800' },
        { code: 'WINCHING', label: 'Winching', color: 'bg-purple-100 text-purple-800' },
    ];

    const getServiceLabel = (code: string) => {
        return availableServices.find(s => s.code === code) || { label: code, color: 'bg-gray-100 text-gray-800' };
    };

    // Recovery Companies Dialog
    if (showRecoveryCompanies) {
        return (
            <AlertDialog open={showRecoveryCompanies} onOpenChange={setShowRecoveryCompanies}>
                <AlertDialogContent className="border-green-500 border-2 max-w-lg max-h-[80vh] overflow-y-auto">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold flex items-center gap-2 text-green-600">
                            <Truck className="w-6 h-6" />
                            Nearby Recovery Companies
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-sm">
                            <span className="font-medium text-foreground">
                                SOS Signal Sent Successfully!
                            </span>
                            <br />
                            Your emergency alert has been broadcasted. Here are the nearest vehicle recovery companies to your location:
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    
                    <div className="space-y-3 my-4">
                        {nearbyCompanies.map((company, index) => (
                            <div key={company.id} className="border rounded-lg p-3 bg-muted/50">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <Badge variant={index === 0 ? "default" : "outline"} className="text-xs">
                                                {index === 0 ? '🏆 Nearest' : `#${index + 1}`}
                                            </Badge>
                                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {company.distanceKm.toFixed(1)} km away
                                            </span>
                                        </div>
                                        <h4 className="font-semibold text-foreground mt-1">{company.name}</h4>
                                        {company.description && (
                                            <p className="text-xs text-muted-foreground mt-0.5">{company.description}</p>
                                        )}
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {company.services.slice(0, 3).map(service => {
                                                const serviceInfo = getServiceLabel(service);
                                                return (
                                                    <span key={service} className={`text-xs px-2 py-0.5 rounded ${serviceInfo.color}`}>
                                                        {serviceInfo.label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        {company.address && (
                                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {company.address}, {company.district}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-3">
                                    <Button 
                                        size="sm" 
                                        className="flex-1"
                                        onClick={() => handleCall(company.phone)}
                                    >
                                        <Phone className="w-4 h-4 mr-1" />
                                        Call {company.phone}
                                    </Button>
                                    {company.phoneTwo && (
                                        <Button 
                                            size="sm" 
                                            variant="outline"
                                            onClick={() => handleCall(company.phoneTwo!)}
                                        >
                                            <Phone className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <AlertDialogFooter>
                        <AlertDialogAction 
                            onClick={() => setShowRecoveryCompanies(false)}
                            className="w-full"
                        >
                            Close
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        );
    }

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                <Button
                    variant="destructive"
                    size="lg"
                    className="h-16 w-16 rounded-full shadow-2xl border-4 border-red-400 animate-pulse hover:animate-none hover:scale-110 transition-transform bg-red-600 hover:bg-red-700"
                >
                    <Siren className="w-8 h-8 text-white" />
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="border-red-500 border-2">
                <AlertDialogHeader>
                    <AlertDialogTitle className="text-2xl font-bold flex items-center gap-2 text-red-600">
                        <AlertTriangle className="w-8 h-8" />
                        EMERGENCY SOS
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-lg">
                        Are you sure you want to trigger an emergency alert?
                        <br /><br />
                        This will notify:
                        <ul className="list-disc pl-5 mt-2 text-foreground font-medium">
                            <li>Fleet Manager</li>
                            <li>Client</li>
                            <li>Platform Administrators</li>
                        </ul>
                        <br />
                        Current Location will be shared.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="gap-4">
                    <AlertDialogCancel className="h-12 text-lg">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => { e.preventDefault(); handleConfirm(); }}
                        className="bg-red-600 hover:bg-red-700 h-12 text-lg font-bold w-full"
                        disabled={triggerSOS.isLoading}
                    >
                        {triggerSOS.isLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Siren className="w-5 h-5 mr-2" />}
                        CONFIRM EMERGENCY
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default SOSButton;
